import { authClient } from "@/shared/http";
import type { AuthSession } from "../domain/authSession";

export type LoginAuthType = "GitHub";

// Overlord `GitHubIdentityResponse` — provider-neutral identity 의 versioned 스냅샷.
// credential 성격의 값은 attributes 에 오지 않는다(Overlord 계약).
export type LoginIdentityResp = {
  version: 1;
  provider: "github";
  subject: string;
  username: string | null;
  display_name: string | null;
  email: string | null;
  email_verified: boolean;
  attributes: Record<string, unknown>;
};

// Overlord `GitHubLoginResponse`.
export type LoginResp = {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
  auth_type: LoginAuthType;
  identity: LoginIdentityResp;
};

export type GithubStartResp = {
  authorize_url: string;
  state: string;
};

export type GithubLoginParams = {
  code: string;
  state: string;
};

type GithubLoginReq = GithubLoginParams & {
  auth_type: "GitHub";
};

export async function startGithubLogin(redirectUri: string): Promise<GithubStartResp> {
  // redirect_uri 를 넘겨 Overlord 가 현재 환경의 콜백 URL 을 authorize_url 에 싣고 token 교환에
  // 재사용하게 한다. allowlist(GITHUB_ALLOWED_REDIRECT_URIS) 밖이면 400 으로 막힌다.
  const response = await authClient.get<GithubStartResp>("/v1/auth/github/start", {
    params: { redirect_uri: redirectUri },
  });

  return response.data;
}

export async function loginWithGithubCode(params: GithubLoginParams): Promise<LoginResp> {
  const response = await authClient.post<LoginResp>("/v1/auth/github/login", {
    auth_type: "GitHub",
    code: params.code,
    state: params.state,
  } satisfies GithubLoginReq);

  return response.data;
}

export function createAuthSession(loginResp: LoginResp): AuthSession {
  const { identity } = loginResp;
  return {
    provider: identity.provider,
    subject: identity.subject,
    username: nonEmpty(identity.username),
    // 표시 이름은 GitHub 프로필 이름 → username → subject 순으로 폴백한다. 프로필 이름을 비워 둔
    // 계정도 사이드바에 무언가는 보여야 한다.
    displayName: nonEmpty(identity.display_name) ?? nonEmpty(identity.username) ?? identity.subject,
    email: nonEmpty(identity.email),
    accessToken: loginResp.access_token,
    // Overlord TTL(초)을 AppState 에서 쓰는 절대 만료 시각으로 바꾼다.
    accessTokenExpiresAt: calculateExpiresAt(loginResp.expires_in),
  };
}

function nonEmpty(value: string | null): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
}

function calculateExpiresAt(expiresInSeconds: number): string {
  return new Date(Date.now() + expiresInSeconds * 1000).toISOString();
}
